import axios from 'axios';
import {
    clearAuthSession,
    getAccessToken,
    getAuthSession,
    getLockedTourId,
    getRefreshToken,
    setAuthSession
} from "./src/vue/services/authSession";

const axiosInstance = axios.create({
        withCredentials: false,
        withXSRFToken: false,
        headers: {
            'X-Tour-Host': location.host,
        }
    }
);

let refreshTokenRequest = null;

function isClientApiUrl(url) {
    if (!url || !process.env.CLIENT_API_URL) return false;
    try {
        const resolvedUrl = new URL(url, window.location.origin);
        const apiBaseUrl = new URL(process.env.CLIENT_API_URL, window.location.origin);
        return resolvedUrl.origin === apiBaseUrl.origin && resolvedUrl.pathname.startsWith(apiBaseUrl.pathname);
    } catch (e) {
        return false;
    }
}

function isAuthEndpoint(url) {
    try {
        const resolvedUrl = new URL(url, window.location.origin);
        return resolvedUrl.pathname.endsWith('/login') || resolvedUrl.pathname.endsWith('/auth/refresh');
    } catch (e) {
        return false;
    }
}

export async function ensureCsrfCookie() {
    return Promise.resolve();
}

async function refreshAccessToken() {
    const refreshToken = getRefreshToken();
    if (!refreshToken || !process.env.CLIENT_API_URL) {
        throw new Error('Missing refresh token');
    }

    if (!refreshTokenRequest) {
        refreshTokenRequest = axios.post(
            process.env.CLIENT_API_URL + '/auth/refresh',
            { refresh_token: refreshToken },
            {
                withCredentials: false,
                headers: {
                    'X-Tour-Host': location.host,
                },
            }
        ).then((response) => {
            const currentSession = getAuthSession() || {};
            const accessToken = response.data?.access_token || response.data?.accessToken || response.data?.token;
            if (!accessToken) {
                throw new Error('Refresh response did not include an access token');
            }
            const rotatedRefreshToken = response.data?.refresh_token || response.data?.refreshToken || currentSession.refreshToken || refreshToken;
            let expiresAt = response.data?.expires_at || response.data?.expiresAt || currentSession.expiresAt || null;
            const expiresIn = response.data?.expires_in || response.data?.expiresIn || response.data?.access_token_expires_in;
            if (!expiresAt && expiresIn) {
                expiresAt = new Date(Date.now() + Number(expiresIn) * 1000).toISOString();
            }
            let refreshExpiresAt =
                response.data?.refresh_token_expires_at ||
                response.data?.refreshTokenExpiresAt ||
                response.data?.refresh_expires_at ||
                response.data?.refreshExpiresAt ||
                currentSession.refreshExpiresAt ||
                null;
            const refreshExpiresIn =
                response.data?.refresh_token_expires_in ||
                response.data?.refreshTokenExpiresIn ||
                response.data?.refresh_expires_in ||
                response.data?.refreshExpiresIn;
            if (!refreshExpiresAt && refreshExpiresIn) {
                refreshExpiresAt = new Date(Date.now() + Number(refreshExpiresIn) * 1000).toISOString();
            }

            setAuthSession({
                ...currentSession,
                accessToken,
                refreshToken: rotatedRefreshToken,
                expiresAt,
                refreshExpiresAt
            });
            return accessToken;
        }).finally(() => {
            refreshTokenRequest = null;
        });
    }

    return refreshTokenRequest;
}

axiosInstance.interceptors.request.use(async (config) => {
    const requestUrl = config.url || '';
    let accessToken = getAccessToken();
    const lockedTourId = getLockedTourId();

    config.headers = config.headers || {};

    if (!accessToken && getRefreshToken() && isClientApiUrl(requestUrl) && !isAuthEndpoint(requestUrl)) {
        try {
            accessToken = await refreshAccessToken();
        } catch (refreshError) {
            clearAuthSession();
            accessToken = null;
        }
    }

    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }

    if (!config.headers['X-Tour-Id'] && lockedTourId) {
        config.headers['X-Tour-Id'] = lockedTourId;
    }

    return config;
});

axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error?.config;
        const statusCode = error?.response?.status;
        const requestUrl = originalRequest?.url || '';
        const isRefreshCall = requestUrl.includes('/auth/refresh');

        if (statusCode === 401 && originalRequest && !originalRequest._authRetried && !isRefreshCall && isClientApiUrl(requestUrl) && getRefreshToken()) {
            originalRequest._authRetried = true;
            try {
                const renewedAccessToken = await refreshAccessToken();
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${renewedAccessToken}`;
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                clearAuthSession();
                return Promise.reject(refreshError);
            }
        }

        // Handle unauthorized responses
        console.log("axiosInstance onRejected", window.location.pathname, statusCode);
        if (error.response && error.response.status === 401) {
            // Redirect to the start screen
            clearAuthSession();
            if (window.location.pathname !== "/") { // TODO check tourId here and filter out redirectTo query param to avoid nested redirectTo values
                window.location.href = `/?redirectTo=${window.location.pathname}`;
            }
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;